import { SQLFilters } from '../../../../api/types';
import { RegularMetricId } from '../../../../api/types-graphql';
import {
  ChartGridConfig,
  KpiFactboxBenchmarkPeriods,
  KpiFactboxBenchmarkTypes,
  KpiV2ComponentConfig,
  KpiV2Type,
  LanguageString,
  LineChartComponentConfig,
  LineChartQueryConfig,
  TimePeriodBarChartComponentConfig,
  TimePeriodBarChartQueryConfig,
} from '../../../../common/components/dashboards/types';
import { Languages } from '../../../../constants';
import { DataFieldWithDataType, TimeSelection, TimeSelectionType } from '../../../../types';
import { getKeyFromDataFieldWithDataType } from '../../../../utils';
import { StoryboardDateUtils } from '../../utils';
import { getFactboxGridConfig, topLineChartGridConfig } from '../chart-layouts';

export const getTurnoverTopChartConfig = (
  storyboardDateUtils: StoryboardDateUtils,
  filters: SQLFilters | null
): LineChartComponentConfig => {
  const { twelveMonthsAgoFromLastMonthEndOfMonth, lastMonthEndOfMonth } = storyboardDateUtils;
  const query: LineChartQueryConfig = {
    metrics: [
      { type: 'RegularMetricId', value: RegularMetricId.Pm0318_2CustomAttritionNikkiMonthly },
      { type: 'RegularMetricId', value: RegularMetricId.Pm0318_4CustomAttritionNikkiLeavers },
    ],
    timeSelection: {
      type: TimeSelectionType.CalendarYearMonthly,
      input: {
        start: twelveMonthsAgoFromLastMonthEndOfMonth,
        end: lastMonthEndOfMonth,
      },
    },
    filters: filters ?? undefined,
  };
  return {
    chartId: 'top-chart',
    title: {
      [Languages.EN]: 'Turnover Historical(% and Leavers)',
      [Languages.JA]: 'Turnover Historical(% and Leavers)',
    },
    chartTypeConfig: {
      chartType: 'line',
    },
    queries: [query],
    ...topLineChartGridConfig,
  };
};

export const getTurnoverByDimensionChartConfig = (
  storyboardDateUtils: StoryboardDateUtils,
  dimension: DataFieldWithDataType,
  title: LanguageString,
  filters: SQLFilters | null,
  gridConfig: ChartGridConfig
): TimePeriodBarChartComponentConfig => {
  const { lastMonthEndOfMonth, twelveMonthsAgoFromLastMonthEndOfMonth } = storyboardDateUtils;
  const query: TimePeriodBarChartQueryConfig = {
    metrics: [
      { type: 'RegularMetricId', value: RegularMetricId.Pm0318_3CustomAttritionNikkiTtm },
      { type: 'RegularMetricId', value: RegularMetricId.Pm0318_4CustomAttritionNikkiLeavers },
    ],
    timeSelection: {
      type: TimeSelectionType.CalendarYearSingleValueByMonths,
      input: {
        start: twelveMonthsAgoFromLastMonthEndOfMonth,
        end: lastMonthEndOfMonth,
      },
    },
    segmentations: [
      {
        type: 'non-hierarchical',
        dataField: dimension,
      },
    ],
    filters: filters ?? undefined,
  };
  return {
    chartId: `by-dimension-chart-${getKeyFromDataFieldWithDataType(dimension)}`,
    chartTypeConfig: {
      chartType: 'bar',
      timeframe: 'timePeriod',
    },
    title,
    queries: [query],
    ...gridConfig,
  };
};

export const getNikkiTTMFactbox = (
  storyboardDateUtils: StoryboardDateUtils,
  filters: SQLFilters | null
): KpiV2ComponentConfig => {
  const { lastMonthEndOfMonth, twelveMonthsAgoFromLastMonthEndOfMonth } = storyboardDateUtils;
  const timeSelection: TimeSelection = {
    type: TimeSelectionType.CalendarYearSingleValueByMonths,
    input: {
      start: twelveMonthsAgoFromLastMonthEndOfMonth,
      end: lastMonthEndOfMonth,
    },
  };
  const kpiConfig: KpiV2ComponentConfig = {
    chartId: 'tenure-factbox',
    chartTypeConfig: {
      chartType: 'kpi-v2',
    },
    title: {
      [Languages.EN]: 'TTM(Trailing Twelve Months) %',
      [Languages.JA]: 'TTM(Trailing Twelve Months) %',
    },
    options: {
      kpiType: KpiV2Type.SINGLE_METRIC,
      section: {
        key: 'tenure',
        query: {
          metrics: [{ type: 'RegularMetricId', value: RegularMetricId.Pm0318_3CustomAttritionNikkiTtm }],
          timeSelection,
          filters: filters ?? undefined,
        },
        benchmarkConfigs: [
          {
            benchmarkPeriod: KpiFactboxBenchmarkPeriods.YEAR_ON_YEAR,
            benchmarkType: KpiFactboxBenchmarkTypes.DELTA,
          },
        ],
      },
    },
    ...getFactboxGridConfig(2),
  };
  return kpiConfig;
};

export const getNikkiLeaversFactbox = (
  storyboardDateUtils: StoryboardDateUtils,
  filters: SQLFilters | null
): KpiV2ComponentConfig => {
  const { lastMonthEndOfMonth, twelveMonthsAgoFromLastMonthEndOfMonth } = storyboardDateUtils;
  const timeSelection: TimeSelection = {
    type: TimeSelectionType.CalendarYearSingleValueByMonths,
    input: {
      start: twelveMonthsAgoFromLastMonthEndOfMonth,
      end: lastMonthEndOfMonth,
    },
  };
  const kpiConfig: KpiV2ComponentConfig = {
    chartId: 'tenure-factbox',
    chartTypeConfig: {
      chartType: 'kpi-v2',
    },
    title: {
      [Languages.EN]: 'Leavers',
      [Languages.JA]: 'Leavers',
    },
    options: {
      kpiType: KpiV2Type.SINGLE_METRIC,
      section: {
        key: 'tenure',
        query: {
          metrics: [{ type: 'RegularMetricId', value: RegularMetricId.Pm0318_4CustomAttritionNikkiLeavers }],
          timeSelection,
          filters: filters ?? undefined,
        },
        benchmarkConfigs: [
          {
            benchmarkPeriod: KpiFactboxBenchmarkPeriods.YEAR_ON_YEAR,
            benchmarkType: KpiFactboxBenchmarkTypes.DELTA,
          },
        ],
      },
    },
    ...getFactboxGridConfig(3),
  };
  return kpiConfig;
};
